import { useMutation, useQuery, useQueryClient } from 'react-query'

import type {
  RepeatableTask,
  RepeatableTaskForm,
} from '@/modules/tasks/service/repeatable-task-service'
import { useRepeatableTaskService } from '@/modules/tasks/service/task-fetch-context'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

export const repeatableTaskQueryKey = 'repeatableTasks'

export function useRepeatableTasks(options?: { enabled: boolean }) {
  const { list } = useRepeatableTaskService()

  return useQuery(repeatableTaskQueryKey, list, options)
}

export function useCreateRepeatableTask() {
  const client = useQueryClient()
  const { create } = useRepeatableTaskService()

  return useMutation(create, {
    onMutate: async (task: RepeatableTaskForm) => {
      const current = client.getQueryData<RepeatableTask[]>(
        repeatableTaskQueryKey,
      )

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = [...validEntries, task]

        client.setQueryData(repeatableTaskQueryKey, updated)
      }

      return current
    },
    onSettled: () => client.invalidateQueries(repeatableTaskQueryKey),
  })
}

export function useDeleteRepeatableTask() {
  const client = useQueryClient()
  const { delete: delete_ } = useRepeatableTaskService()

  return useMutation(delete_, {
    onMutate: async (id: number) => {
      const current = client.getQueryData<RepeatableTask[]>(
        repeatableTaskQueryKey,
      )

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = validEntries.filter((entry) => entry.id !== id)

        client.setQueryData(repeatableTaskQueryKey, updated)
      }

      return current
    },
    onSettled: () => client.invalidateQueries(repeatableTaskQueryKey),
  })
}

interface UseUpdateRepeatableTaskSettings {
  onlyInvalidateOnError: boolean
}
const defaultUpdateTaskSettings: UseUpdateRepeatableTaskSettings = {
  onlyInvalidateOnError: false,
}

export function useUpdateRepeatableTask(settings = defaultUpdateTaskSettings) {
  const { onlyInvalidateOnError } = settings
  const client = useQueryClient()
  const { update } = useRepeatableTaskService()

  return useMutation(update, {
    onMutate: async ({ id, ...form }) => {
      const current = client.getQueryData<RepeatableTask[]>(
        repeatableTaskQueryKey,
      )

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = validEntries.map((entry) =>
          entry.id === id ? { ...entry, ...form } : entry,
        )

        client.setQueryData(repeatableTaskQueryKey, updated)
      }

      return current
    },
    onError: () => {
      if (onlyInvalidateOnError) {
        return client.invalidateQueries(repeatableTaskQueryKey)
      }
    },
    onSettled: onlyInvalidateOnError
      ? undefined
      : () => client.invalidateQueries(repeatableTaskQueryKey),
  })
}
